/**
 * Λεπτομέρειες τραπεζιού για τον σερβιτόρο: τι έχει παραγγείλει το τραπέζι,
 * τι είναι έτοιμο να σερβιριστεί, και οι δύο ενέργειες που κάνει πραγματικά
 * ένας σερβιτόρος στο πάτωμα — νέα παραγγελία και «ζητάει λογαριασμό».
 *
 * Ό,τι αφορά ταμείο (εκπτώσεις, ακυρώσεις, παραστατικά, split πληρωμές) ΔΕΝ
 * είναι εδώ: γίνεται από την κονσόλα ταμία.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Users, Clock, Check, Loader2, Receipt, AlertCircle, Banknote, FileText, MessageSquare } from 'lucide-react';
import { useToast } from '../../shared/Toast';
import { dineInApi, DineInApiError } from '../../../services/dineInApi';
import { buildBillSummary, selectPayableEntries, tablePaymentState } from '../../../utils/dineInMoney';
import { showSizeName } from '../../../utils/sellableSizes';
import type { Product } from '../../../types/order';
import { Sheet, Button, EmptyState, money } from './ui';
import PaymentScreen from './PaymentScreen';
import KitchenMessageSheet from './KitchenMessageSheet';
import { itemModifiers, hasModifiers } from './itemModifiers';
import ReservationStrip from './ReservationStrip';
import type { FloorReservation } from '../../../utils/dineInReservations';
import { cardDoubtsOf, hasCardInFlight, CARD_DOUBT_POLL_MS } from '../../../utils/dineInCardDoubt';
import CardDoubtBanner from './CardDoubtBanner';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Αναμονή',
  sent_to_kitchen: 'Στην κουζίνα',
  preparing: 'Ετοιμάζεται',
  ready: 'Έτοιμη',
  served: 'Σερβιρίστηκε',
  cancelled: 'Ακυρωμένη',
};

const STATUS_STYLES: Record<string, string> = {
  ready: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300',
  preparing: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
  sent_to_kitchen: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
  served: 'bg-surface-3 dark:bg-surface-2 text-t3',
  cancelled: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
};

interface TableSheetProps {
  storeId: number;
  table: any;
  /** Η κράτηση που κάθεται στο τραπέζι (ή η επόμενη, αν είναι ελεύθερο) — Xentrix Sala */
  reservation?: FloorReservation | null;
  /** dine_in_settings.guest_count_enabled — κλειστό: ούτε δείχνουμε ούτε ζητάμε άτομα */
  guestCountEnabled?: boolean;
  /** dine_in_settings.bill_print_enabled — κουμπί «Λογαριασμός» (μη φορολογικό χαρτί) */
  billPrintEnabled?: boolean;
  /** Τα έτοιμα μηνύματα κουζίνας· null = η λειτουργία είναι κλειστή (utils/kitchenMessages) */
  kitchenMessages?: string[] | null;
  /** dine_in_print_receipt — ο σερβιτόρος εκδίδει ο ίδιος την απόδειξη */
  canIssueReceipt: boolean;
  /** dine_in_process_payment — ο σερβιτόρος εισπράττει ο ίδιος */
  canTakePayment: boolean;
  onClose: () => void;
  /** Το τραπέζι άλλαξε — ανανέωσε τη λίστα στο app */
  onChanged: () => void;
  /** Άνοιγμα οθόνης παραγγελίας για αυτό το τραπέζι */
  onNewOrder: (sessionId: number | null, guestCount: number) => void;
  /** Αύξοντας αριθμός που αλλάζει όταν έρθει socket event — προκαλεί refetch */
  refreshSignal: number;
  /** Ο κατάλογος του app — για να βρεθεί αν το μέγεθος μιας γραμμής είναι το μοναδικό (απόφαση 15) */
  products?: Product[];
}

const TableSheet: React.FC<TableSheetProps> = ({
  storeId,
  table,
  reservation = null,
  guestCountEnabled = false,
  billPrintEnabled = false,
  kitchenMessages = null,
  canIssueReceipt,
  canTakePayment,
  onClose,
  onChanged,
  onNewOrder,
  refreshSignal,
  products = [],
}) => {
  const { showToast } = useToast();
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [guestCount, setGuestCount] = useState(2);

  const load = useCallback(async () => {
    try {
      const data = await dineInApi.tableDetails(storeId, table.id);
      setDetails(data);
      if (data.session?.guest_count) setGuestCount(data.session.guest_count);
    } catch (error) {
      console.error('Failed to load table details:', error);
      showToast('error', 'Δεν φόρτωσαν τα στοιχεία του τραπεζιού.');
    } finally {
      setLoading(false);
    }
  }, [storeId, table.id, showToast]);

  useEffect(() => {
    load();
  }, [load, refreshSignal]);

  const session = details?.session || null;
  const orders = details?.orders || [];

  // Κάρτα του τραπεζιού από τον server (και μετά από ανανέωση του app). Όσο τρέχει ακόμη, το τραπέζι ξαναφορτώνεται
  // ώσπου να βγει αποτέλεσμα (πληρωμή ή «αβέβαιη») — και όταν είναι ανοιχτή η οθόνη είσπραξης, που παίρνει τα ίδια στοιχεία.
  const cardDoubts = cardDoubtsOf(session);
  const cardInFlight = hasCardInFlight(cardDoubts);
  useEffect(() => {
    if (!cardInFlight) return;
    const timer = setTimeout(load, CARD_DOUBT_POLL_MS);
    return () => clearTimeout(timer);
  }, [cardInFlight, details, load]);
  const liveOrders = orders.filter((o: any) => o.status !== 'cancelled');
  // Το σύνολο του τραπεζιού (ένα νούμερο παντού): ίδιο με την οθόνη είσπραξης, όχι το άθροισμα
  // τιμών μονάδας που στρογγυλεύονται χωριστά με την έκπτωση (59,48 αντί 59,47 στο τραπέζι 52, 5/10/2026).
  const total = session ? tablePaymentState(details).total : 0;
  const itemCount = selectPayableEntries(orders).reduce(
    (s, { item }) => s + (parseInt(String(item.quantity ?? 1), 10) || 1),
    0
  );

  /** Μετά την έκδοση απόδειξης η σέσια κλειδώνει — δεν μπαίνουν νέοι γύροι. */
  const sessionLocked =
    !!session && session.status !== 'open';

  // «Πρώτα απόδειξη, μετά είσπραξη — παντού» (1/10/2026). Η «Είσπραξη»
  // εμφανιζόταν σε κάθε ανοιχτό τραπέζι: ο σερβιτόρος εξοφλούσε, η συνεδρία
  // έκλεινε και απόδειξη δεν μπορούσε πια να βγει. Τώρα, όπως στο ταμείο, μόνο
  // μετά το παραστατικό — ο server απαντά 409 receipt_required σε κάθε άλλη
  // περίπτωση. Η εκπαίδευση δεν εκδίδει ποτέ παραστατικό, άρα εισπράττει όπως πριν.
  //
  // Η απόφαση έρχεται από τον server (session.payment_unlocked — ο ΙΔΙΟΣ κανόνας
  // με τα routes πληρωμής): καλύπτει και ό,τι το status δεν λέει — εκδομένη 11.1
  // σε πτώση του παρόχου (η συνεδρία μένει 'open'), κατάστημα χωρίς ηλεκτρονικό
  // πάροχο. Χωρίς το πεδίο (παλιός server) → ο κανόνας του status.
  const receiptIssued =
    !!session && (session.status === 'receipt_printed' || session.status === 'invoice_printed');
  const isTraining = session?.is_training === true;
  const paymentUnlocked =
    typeof session?.payment_unlocked === 'boolean' ? session.payment_unlocked : receiptIssued || isTraining;
  const canCollect = !!session && canTakePayment && paymentUnlocked;
  // Σύνολο 0 € (όλα κερασμένα ή έκπτωση 100%): ούτε απόδειξη ούτε είσπραξη — το τραπέζι απλώς κλείνει.
  const zeroTotal = !!session && liveOrders.length > 0 && total <= 0.005;
  const needsReceiptHint = !!session && canTakePayment && !paymentUnlocked && !zeroTotal;

  const markServed = async (orderId: number) => {
    setBusy(`serve-${orderId}`);
    try {
      await dineInApi.serveOrder(storeId, orderId);
      await load();
      onChanged();
    } catch (error) {
      showToast('error', error instanceof DineInApiError ? error.message : 'Αποτυχία ενημέρωσης.');
    } finally {
      setBusy(null);
    }
  };

  // Έκδοση απόδειξης από το κινητό. ΚΛΕΙΔΩΝΕΙ το τραπέζι — γι' αυτό ζητάμε
  // επιβεβαίωση με δεύτερο πάτημα αντί για σκέτο κουμπί: ένα κατά λάθος άγγιγμα
  // σε οθόνη αφής θα έκοβε παραστατικό που δεν ξεγίνεται.
  const [confirmReceipt, setConfirmReceipt] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  useEffect(() => {
    if (!confirmReceipt) return;
    const t = setTimeout(() => setConfirmReceipt(false), 4000);
    return () => clearTimeout(t);
  }, [confirmReceipt]);

  const issueReceipt = async () => {
    if (!session) return;
    if (!confirmReceipt) { setConfirmReceipt(true); return; }
    setConfirmReceipt(false);
    setBusy('receipt');
    try {
      // Ο server απαντά 200 ΚΑΙ σε αποτυχία του παρόχου (success:false) — πριν,
      // κάθε 200 έγραφε «εκδόθηκε και τυπώνεται», ακόμη και σε πτώση όπου δεν
      // τυπώνεται τίποτα, και ο σερβιτόρος ξαναπατούσε το κουμπί.
      const res: any = await dineInApi.issueReceipt(storeId, session.id);
      if (res?.success === false) {
        if (res.issued) {
          // Εκδόθηκε (με αριθμό), περιμένει διαβίβαση· η είσπραξη επιτρέπεται.
          showToast('warning', res.message || 'Το παραστατικό εκδόθηκε χωρίς απάντηση από τον πάροχο.');
          await load();
          onChanged();
        } else {
          showToast('error', res.message || 'Αποτυχία έκδοσης απόδειξης.');
        }
        return;
      }
      showToast('success', 'Η απόδειξη εκδόθηκε και τυπώνεται.');
      onChanged();
      onClose();
    } catch (error) {
      // 409 «υπάρχει ήδη παραστατικό»: ξαναφόρτωσε — η «Είσπραξη» φαίνεται πια.
      if (error instanceof DineInApiError && error.body?.existing_fiscal_document_id) load();
      showToast('error', error instanceof DineInApiError ? error.message : 'Αποτυχία έκδοσης απόδειξης.');
    } finally {
      setBusy(null);
    }
  };

  // Λογαριασμός για το τραπέζι ΠΡΙΝ την απόδειξη — ίδια ποσά με την πληρωμή (dineInMoney).
  const printBill = async () => {
    if (!session) return;
    const bill = buildBillSummary(details?.orders || [], session.total_amount, details?.paidItems || []);
    if (bill.lines.length === 0) {
      showToast('warning', 'Το τραπέζι δεν έχει είδη για λογαριασμό.');
      return;
    }
    setBusy('print-bill');
    try {
      const res = await dineInApi.printBill(storeId, session.id, bill);
      showToast('success', `Ο λογαριασμός τυπώνεται (${res.printer}).`);
    } catch (error) {
      if (error instanceof DineInApiError && error.code === 'bill_stale') load();
      showToast('error', error instanceof DineInApiError ? error.message : 'Αποτυχία εκτύπωσης λογαριασμού.');
    } finally {
      setBusy(null);
    }
  };

  // Μήνυμα κουζίνας μόνο του («ΠΑΣΟ ΚΥΡΙΩΣ» …): μικρό δελτίο σε όλους τους εκτυπωτές κουζίνας.
  const [messageSheet, setMessageSheet] = useState(false);
  const sendKitchenMessage = async (messages: string[]) => {
    if (!session || messages.length === 0) return;
    setBusy('kitchen-message');
    try {
      const res = await dineInApi.sendKitchenMessage(storeId, session.id, messages);
      showToast('success', `Στάλθηκε στην κουζίνα: ${res.messages.join(' · ')}`);
      setMessageSheet(false);
    } catch (error) {
      showToast('error', error instanceof DineInApiError ? error.message : 'Αποτυχία αποστολής μηνύματος.');
    } finally {
      setBusy(null);
    }
  };
  // Και μετά την απόδειξη: το τραπέζι συνήθως τρώει ακόμη («ΠΑΣΟ ΓΛΥΚΟ»).
  const messageButton = session && kitchenMessages ? (
    <Button tone="neutral" full onClick={() => setMessageSheet(true)}>
      <MessageSquare className="w-5 h-5" />
      Μήνυμα κουζίνας
    </Button>
  ) : null;

  const closeZeroTable = async () => {
    if (!session) return;
    setBusy('close-zero');
    try {
      await dineInApi.closeSession(storeId, session.id);
      showToast('success', `Το τραπέζι έκλεισε (σύνολο ${money(0)}).`);
      onChanged();
      onClose();
    } catch (error) {
      showToast('error', error instanceof DineInApiError ? error.message : 'Αποτυχία κλεισίματος τραπεζιού.');
    } finally {
      setBusy(null);
    }
  };
  const zeroCloseButton = zeroTotal ? (
    canTakePayment ? (
      <Button tone="success" full disabled={busy === 'close-zero'} onClick={closeZeroTable}>
        {busy === 'close-zero' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
        Κλείσιμο τραπεζιού ({money(0)})
      </Button>
    ) : (
      <p className="text-center text-[13px] text-t3 px-1">Σύνολο {money(0)} — το τραπέζι κλείνει από το ταμείο.</p>
    )
  ) : null;

  const openedAt = session?.opened_at
    ? new Date(session.opened_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })
    : null;

  const subtitle = session
    ? [guestCountEnabled && `${session.guest_count} άτομα`, openedAt && `από ${openedAt}`].filter(Boolean).join(' · ')
    : 'Ελεύθερο τραπέζι';

  const footer = (
    <div className="space-y-2 pb-1">
      <CardDoubtBanner doubts={cardDoubts} />
      {sessionLocked ? (
        <>
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700/50 rounded-xl text-amber-900 dark:text-amber-200 text-sm">
            <Receipt className="w-5 h-5 shrink-0 mt-0.5" />
            <span>Έχει εκδοθεί παραστατικό. Για αλλαγές, ο λογαριασμός πάει από το ταμείο.</span>
          </div>
          {/* Μετά το παραστατικό η είσπραξη είναι η ΜΟΝΗ επιτρεπτή ενέργεια —
              χωρίς αυτό το κουμπί ο σερβιτόρος έμενε σε αδιέξοδο. */}
          {zeroCloseButton}
          {canCollect && !zeroTotal && (
            <Button tone="success" full onClick={() => setShowPayment(true)}>
              <Banknote className="w-5 h-5" />
              Είσπραξη
            </Button>
          )}
          {messageButton}
        </>
      ) : (
        <>
          <Button
            tone="primary"
            full
            onClick={() => onNewOrder(session?.id ?? null, guestCount)}
          >
            <Plus className="w-5 h-5" />
            Νέα παραγγελία
          </Button>
          {messageButton}
          {zeroCloseButton}
          {session && billPrintEnabled && !zeroTotal && (
            <Button tone="neutral" full disabled={busy === 'print-bill'} onClick={printBill}>
              {busy === 'print-bill' ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Λογαριασμός
            </Button>
          )}
          {session && canIssueReceipt && !zeroTotal && (
            <Button tone={confirmReceipt ? 'danger' : 'warning'} full disabled={busy === 'receipt'} onClick={issueReceipt}>
              {busy === 'receipt' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Receipt className="w-5 h-5" />}
              {confirmReceipt ? 'Σίγουρα; Πάτησε ξανά' : 'Έκδοση απόδειξης'}
            </Button>
          )}
          {/* Ανοιχτό τραπέζι: «Είσπραξη» μόνο στην εκπαίδευση (δεν έχει παραστατικό). */}
          {canCollect && !zeroTotal && (
            <Button tone="success" full onClick={() => setShowPayment(true)}>
              <Banknote className="w-5 h-5" />
              Είσπραξη
            </Button>
          )}
          {/* Το «Ζήτησε λογαριασμό» (για όποιον δεν εκδίδει) καταργήθηκε 12/10/2026 — απόφαση ιδιοκτήτη. */}
          {needsReceiptHint && (
            <p data-testid="receipt-first-hint" className="flex items-center justify-center gap-1.5 text-[13px] text-t3 px-1">
              <Receipt className="w-4 h-4 shrink-0" />
              Για είσπραξη χρειάζεται πρώτα απόδειξη
            </p>
          )}
        </>
      )}
    </div>
  );

  if (messageSheet && session && kitchenMessages) {
    return (
      <KitchenMessageSheet
        tableNumber={table.table_number}
        presets={kitchenMessages}
        mode="send"
        busy={busy === 'kitchen-message'}
        onConfirm={sendKitchenMessage}
        onClose={() => setMessageSheet(false)}
      />
    );
  }

  if (showPayment && details) {
    return (
      <PaymentScreen
        storeId={storeId}
        details={details}
        tableNumber={table.table_number}
        products={products}
        onClose={() => setShowPayment(false)}
        onPaid={() => { load(); onChanged(); }}
      />
    );
  }

  return (
    <Sheet
      title={`Τραπέζι ${table.table_number}`}
      subtitle={subtitle}
      onClose={onClose}
      footer={footer}
    >
      {reservation && <ReservationStrip reservation={reservation} />}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : !session ? (
        <div className="px-3">
          <EmptyState
            icon={Users}
            title="Δεν έχει ανοίξει λογαριασμός"
            hint="Πάτησε «Νέα παραγγελία» για να ξεκινήσεις."
          />
          {guestCountEnabled && (
          <div className="role-mobile-card flex items-center justify-between bg-surface-1 rounded-2xl p-3 mb-3">
            <span className="text-t2 font-medium">Άτομα</span>
            <div className="flex items-center bg-surface-2 border border-line-strong dark:border-transparent rounded-xl overflow-hidden">
              <button
                onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                className="w-11 h-11 text-xl font-bold text-t2 active:bg-surface-3"
                aria-label="Λιγότερα άτομα"
              >
                −
              </button>
              <span className="w-10 text-center text-lg font-bold text-t1">{guestCount}</span>
              <button
                onClick={() => setGuestCount(guestCount + 1)}
                className="w-11 h-11 text-xl font-bold text-t2 active:bg-surface-3"
                aria-label="Περισσότερα άτομα"
              >
                +
              </button>
            </div>
          </div>
          )}
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {/* Σύνολο λογαριασμού */}
          <div className="flex items-center justify-between bg-surface-1 rounded-2xl px-3 py-3">
            <div>
              <p className="text-sm text-t3">Λογαριασμός</p>
              <p className="text-[13px] text-t4">{itemCount} προϊόντα</p>
            </div>
            <span className="text-3xl font-bold text-t1 tabular-nums">{money(total)}</span>
          </div>

          {liveOrders.length === 0 ? (
            <EmptyState icon={AlertCircle} title="Καμία παραγγελία ακόμα" />
          ) : (
            liveOrders.map((order: any) => (
              <div key={order.id} className="role-mobile-card bg-surface-1 rounded-2xl p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-bold text-base text-t1">#{order.order_number}</span>
                    <span className="text-sm text-t4">
                      {new Date(order.created_at).toLocaleTimeString('el-GR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-[13px] font-bold shrink-0 ${
                      STATUS_STYLES[order.status] || 'bg-surface-3 dark:bg-surface-2 text-t2'
                    }`}
                  >
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {(order.items || []).map((item: any) => {
                    const mods = itemModifiers(item);
                    return (
                    <div key={item.id} className="flex justify-between gap-3 text-[15px]">
                      <div className={`min-w-0 ${item.is_treat ? 'text-emerald-600 dark:text-emerald-400' : 'text-t1'}`}>
                        <span className="font-bold text-base">{item.quantity}×</span> {item.product_name}
                        {item.size_name && showSizeName(products, item.product_id, { id: item.size_id, name: item.size_name }) && (
                          <span className="text-t4"> ({item.size_name})</span>
                        )}
                        {item.is_treat && <span className="ml-1 text-sm">· κέρασμα</span>}
                        {hasModifiers(mods) && (
                          <div className="mt-0.5 space-y-0.5 text-[13px]" data-testid={`item-mods-${item.id}`}>
                            {mods.options.map((o, i) => (
                              <p key={`o${i}`} className="font-semibold text-indigo-700 dark:text-indigo-300">• {o}</p>
                            ))}
                            {mods.removed.map((r, i) => (
                              <p key={`r${i}`} className="font-semibold text-red-700 dark:text-red-300">χωρίς {r}</p>
                            ))}
                            {mods.note && <p className="text-amber-700 dark:text-amber-400">📝 {mods.note}</p>}
                          </div>
                        )}
                      </div>
                      <span className="text-t2 shrink-0 font-bold tabular-nums">
                        {item.is_treat ? '—' : money(item.total_price)}
                      </span>
                    </div>
                    );
                  })}
                </div>

                {order.status === 'ready' && (
                  <Button
                    tone="success"
                    full
                    className="mt-2"
                    disabled={busy === `serve-${order.id}`}
                    onClick={() => markServed(order.id)}
                  >
                    {busy === `serve-${order.id}` ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                    Σερβιρίστηκε
                  </Button>
                )}
              </div>
            ))
          )}

          {openedAt && (
            <p className="flex items-center justify-center gap-1.5 text-[13px] text-t4 pt-1">
              <Clock className="w-3.5 h-3.5" />
              Άνοιξε στις {openedAt}
            </p>
          )}
        </div>
      )}
    </Sheet>
  );
};

export default TableSheet;
